import { Popover, PopoverProps } from "@mui/material";
import FocusableBox, { FocusableBoxProps } from "./focusable-box";
import { ReactNode, SyntheticEvent, useCallback, useRef, useState } from "react";
import { mergeRefs } from "@/shared/utils";

export interface OpenableInputProps extends Omit<ControlledOpenableInputProps, "open"> {
    open?: boolean,
    defaultOpen?: boolean,
}

export default function OpenableInput({
    open,
    defaultOpen,
    onOpen,
    onClose,
    ...props
}: OpenableInputProps) {
    if (open != null) {
        //Controlled
        return <ControlledOpenableInput open={open} onOpen={onOpen} onClose={onClose} {...props} />
    }

    //Uncontrolled
    return <OpenHook defaultOpen={defaultOpen}>
        {(open, setOpen) => <ControlledOpenableInput
            open={open}
            onOpen={e => {
                onOpen && onOpen(e);
                if (!e.defaultPrevented) setOpen(true);
            }}
            onClose={e => {
                onClose && onClose(e);
                if (!e.defaultPrevented) setOpen(false);
            }}
            {...props}
        />}
    </OpenHook>
}

//#region Open Hook
interface OpenHookProps {
    children?: (open: boolean, setOpen: (open: boolean) => void) => ReactNode,
    defaultOpen?: boolean,
}

function OpenHook({
    defaultOpen = false,
    children
}: OpenHookProps) {
    const [open, setOpen] = useState(defaultOpen);
    return children && children(open, setOpen);
}
//#endregion

//#region Controlled Openable Input
interface ControlledOpenableInputProps extends FocusableBoxProps {
    slotProps?: FocusableBoxProps["slotProps"] & {
        popover?: Omit<PopoverProps, "open">
    },

    boxChildren?: ReactNode | ((open: boolean, setOpen: (open: boolean, e: SyntheticEvent) => void) => ReactNode),
    popoverChildren?: ReactNode | ((open: boolean, setOpen: (open: boolean, e: SyntheticEvent) => void) => ReactNode),

    open: boolean,
    onOpen?: (e: SyntheticEvent) => any,
    onClose?: (e: SyntheticEvent) => any
}

function ControlledOpenableInput({
    open,
    onOpen = () => { },
    onClose = () => { },

    boxRef,
    inputRef,

    boxChildren,
    popoverChildren,
    slotProps: receivedSlotProps = {},

    ...props
}: ControlledOpenableInputProps) {
    const { popover, ...slotProps } = receivedSlotProps;
    const innerBoxRef = useRef<HTMLElement>(null);
    const anchorEl = innerBoxRef.current;

    const handleSetOpen = useCallback((newOpen: boolean, e: SyntheticEvent) => {
        if (props.disabled) return;
        if (newOpen == open) return;
        if (!open && newOpen) onOpen(e);
        if (open && !newOpen) onClose(e);
    }, [open]);

    return <>
        <FocusableBox
            display={"flex"}
            slotProps={slotProps}
            inputRef={inputRef}
            boxRef={mergeRefs(boxRef, innerBoxRef)}
            {...props}
            onClick={e => {
                props.onClick && props.onClick(e);
                if (e.defaultPrevented) return;
                handleSetOpen(true, e);
            }}
            onKeyDown={e => {
                props.onKeyDown && props.onKeyDown(e);
                if (e.defaultPrevented) return;
                if (e.key === " " || e.key === "ArrowUp" || e.key === "ArrowDown") 
                    handleSetOpen(true, e);
            }}
        >
            {typeof boxChildren === "function" ? boxChildren(open, handleSetOpen) : boxChildren}
        </FocusableBox>
        <Popover
            open={open && Boolean(anchorEl)}
            onClose={e => handleSetOpen(false, e as any)}
            anchorEl={anchorEl}
            {...popover}
            anchorOrigin={{
                horizontal: "left",
                vertical: "bottom",
                ...popover?.anchorOrigin
            }}
            slotProps={{
                ...popover?.slotProps,
                paper: {
                    ...popover?.slotProps?.paper,
                    sx: {
                        ...(popover?.slotProps?.paper as any)?.sx,
                        width: anchorEl?.clientWidth
                    }
                }
            }}
        >
            {typeof popoverChildren === "function" ? popoverChildren(open, handleSetOpen) : popoverChildren}
        </Popover>
    </>
}
//#endregion